import { useState, useEffect, useCallback, type FormEvent } from "react";
import {
  commitVersion,
  getBranchVersions,
  getVersion,
  getHeadId,
  checkout,
  observeVersions,
  getCurrentBranch,
  getBranches,
  createBranch,
  switchBranch,
  deleteBranch,
  canFastForward,
  fastForwardMerge,
  type VersionNode,
} from "./versionGraph";

interface VersionPanelProps {
  /** Return the current editor script content for committing. */
  getCurrentScript: () => string;
  /** Return serialized feature tree (JSON) for committing alongside script. */
  getCurrentFeatures: () => string;
  /** Called after checkout so the editor can load the new script + features. */
  onCheckout: (script: string, features?: string) => void;
  /** Called to open a diff view comparing a version's script against HEAD. */
  onCompare?: (original: string, modified: string, originalLabel: string, modifiedLabel: string) => void;
}

function shortHash(id: string): string {
  return id.slice(0, 7);
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function VersionPanel({
  getCurrentScript,
  getCurrentFeatures,
  onCheckout,
  onCompare,
}: VersionPanelProps) {
  const [versions, setVersions] = useState<VersionNode[]>([]);
  const [headId, setHeadId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [committing, setCommitting] = useState(false);
  const [activeBranch, setActiveBranch] = useState("main");
  const [branchMap, setBranchMap] = useState<Record<string, string>>({});
  const [newBranchName, setNewBranchName] = useState("");
  const [showBranchCreate, setShowBranchCreate] = useState(false);

  const refresh = useCallback(() => {
    const branch = getCurrentBranch();
    setActiveBranch(branch);
    setBranchMap(getBranches());
    setVersions(getBranchVersions(branch));
    setHeadId(getHeadId());
  }, []);

  useEffect(() => {
    refresh();
    return observeVersions(refresh);
  }, [refresh]);

  /** Restore script + features from a version node and refresh UI. */
  const restoreVersion = useCallback(
    (node: VersionNode) => {
      onCheckout(node.script, node.features);
      refresh();
    },
    [onCheckout, refresh]
  );

  const handleCommit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const msg = message.trim();
      if (!msg) return;
      setCommitting(true);
      try {
        const script = getCurrentScript();
        const features = getCurrentFeatures();
        await commitVersion(script, msg, "local", features);
        setMessage("");
        refresh();
      } finally {
        setCommitting(false);
      }
    },
    [message, getCurrentScript, getCurrentFeatures, refresh]
  );

  const handleCheckout = useCallback(
    (id: string) => {
      const node = checkout(id);
      if (node) restoreVersion(node);
    },
    [restoreVersion]
  );

  const handleCompare = useCallback(
    (versionId: string) => {
      if (!onCompare || !headId) return;
      const versionNode = getVersion(versionId);
      const headNode = getVersion(headId);
      if (!versionNode || !headNode) return;
      onCompare(
        versionNode.script,
        headNode.script,
        `${shortHash(versionId)} — ${versionNode.message}`,
        `${shortHash(headId)} (HEAD)`
      );
    },
    [onCompare, headId]
  );

  const handleCreateBranch = useCallback(() => {
    const name = newBranchName.trim();
    if (!name || branchMap[name]) return;
    if (!getHeadId()) return;
    createBranch(name);
    switchBranch(name);
    setNewBranchName("");
    setShowBranchCreate(false);
    refresh();
  }, [newBranchName, branchMap, refresh]);

  const handleSwitchBranch = useCallback(
    (branch: string) => {
      const versionId = switchBranch(branch);
      if (!versionId) return;
      const node = getVersion(versionId);
      if (node) restoreVersion(node);
      else refresh();
    },
    [restoreVersion, refresh]
  );

  const handleDeleteBranch = useCallback(
    (branch: string) => {
      if (branch === "main") return;
      deleteBranch(branch);
      if (activeBranch === branch) handleSwitchBranch("main");
      else refresh();
    },
    [activeBranch, handleSwitchBranch, refresh]
  );

  const handleMerge = useCallback(() => {
    if (activeBranch === "main") return;
    const merged = fastForwardMerge(activeBranch, "main");
    if (merged) handleSwitchBranch("main");
  }, [activeBranch, handleSwitchBranch]);

  const mergeAvailable = activeBranch !== "main" && canFastForward(activeBranch, "main");
  const canCreateBranchNow = !!getHeadId() && !!newBranchName.trim() && !branchMap[newBranchName.trim()];
  const branchNames = Object.keys(branchMap);

  return (
    <div className="version-panel">
      {/* ── Commit form (hero action) ── */}
      <form className="version-commit-form" onSubmit={handleCommit}>
        <input
          className="version-commit-input"
          type="text"
          placeholder="Describe your changes..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={committing}
        />
        <button
          className="version-commit-btn"
          type="submit"
          disabled={committing || !message.trim()}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
            <circle cx="8" cy="8" r="3"/>
            <path d="M8 2v3M8 11v3"/>
          </svg>
          {committing ? "Saving..." : "Commit"}
        </button>
      </form>

      {/* ── Branch bar ── */}
      <div className="version-branch-bar">
        <button
          className={`version-branch-chip ${activeBranch !== "main" ? "version-branch-chip--alt" : ""}`}
          onClick={() => {
            const el = document.querySelector(".version-branch-dropdown") as HTMLSelectElement;
            el?.focus();
            el?.click();
          }}
          title="Switch branch"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="11" height="11">
            <circle cx="5" cy="4" r="1.5"/><circle cx="5" cy="12" r="1.5"/><circle cx="11" cy="8" r="1.5"/>
            <path d="M5 5.5v5M5 5.5c3 0 6 1 6 2.5"/>
          </svg>
          {activeBranch}
        </button>
        <select
          className="version-branch-dropdown"
          value={activeBranch}
          onChange={(e) => handleSwitchBranch(e.target.value)}
          aria-label="Switch branch"
        >
          {branchNames.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>

        <div className="version-branch-actions">
          {mergeAvailable && (
            <button className="version-merge-btn" onClick={handleMerge} title={`Fast-forward merge ${activeBranch} → main`}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="11" height="11">
                <path d="M5 4v8M11 4v4c0 2-2 3-4 4"/>
                <circle cx="5" cy="4" r="1.5"/><circle cx="11" cy="4" r="1.5"/><circle cx="5" cy="12" r="1.5"/>
              </svg>
              Merge
            </button>
          )}
          <button
            className={`version-branch-action-btn ${showBranchCreate ? "active" : ""}`}
            onClick={() => setShowBranchCreate(!showBranchCreate)}
            title="Create new branch"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
              <path d="M8 3v10M3 8h10"/>
            </svg>
          </button>
          {activeBranch !== "main" && (
            <button
              className="version-branch-action-btn version-branch-action-btn--danger"
              onClick={() => handleDeleteBranch(activeBranch)}
              title={`Delete branch "${activeBranch}"`}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                <path d="M3 5h10M5.5 5V3.5a1 1 0 011-1h3a1 1 0 011 1V5M6.5 7.5v4M9.5 7.5v4M4.5 5l.5 8.5a1 1 0 001 .5h4a1 1 0 001-.5L11.5 5"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Collapsible branch create ── */}
      {showBranchCreate && (
        <div className="version-branch-create">
          <input
            className="version-branch-input"
            type="text"
            placeholder="Branch name..."
            value={newBranchName}
            onChange={(e) => setNewBranchName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateBranch();
              if (e.key === "Escape") { setShowBranchCreate(false); setNewBranchName(""); }
            }}
            autoFocus
          />
          <button
            className="version-branch-create-btn"
            onClick={handleCreateBranch}
            disabled={!canCreateBranchNow}
          >
            Create
          </button>
        </div>
      )}

      {/* ── Version timeline ── */}
      <div className="version-list">
        {versions.length === 0 && (
          <div className="version-empty">
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1" width="32" height="32">
              <circle cx="16" cy="8" r="3"/>
              <circle cx="16" cy="24" r="3"/>
              <path d="M16 11v10" strokeDasharray="2 2"/>
            </svg>
            <span>No commits yet</span>
            <span className="version-empty-hint">Make changes, then commit to start tracking history</span>
          </div>
        )}
        {versions.map((v, i) => (
          <div
            key={v.id}
            className={`version-item ${v.id === headId ? "version-item--head" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => handleCheckout(v.id)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCheckout(v.id); }}
            title={`Checkout ${shortHash(v.id)}`}
          >
            {/* Timeline rail */}
            <div className="version-rail">
              <div className={`version-dot ${v.id === headId ? "version-dot--head" : ""}`} />
              {i < versions.length - 1 && <div className="version-rail-line" />}
            </div>

            <div className="version-item-body">
              <div className="version-item-top">
                <span className="version-message">{v.message}</span>
                {v.id === headId && <span className="version-head-badge">HEAD</span>}
              </div>
              <div className="version-item-meta">
                <span className="version-hash">{shortHash(v.id)}</span>
                <span className="version-meta-sep">&middot;</span>
                <span className="version-author">{v.author}</span>
                <span className="version-meta-sep">&middot;</span>
                <span className="version-time">{timeAgo(v.timestamp)}</span>
              </div>
              {v.id !== headId && onCompare && (
                <button
                  className="version-compare-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompare(v.id);
                  }}
                  title={`Compare ${shortHash(v.id)} with HEAD`}
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="10" height="10">
                    <path d="M6 3H3v10h3M10 3h3v10h-3M6 8h4"/>
                  </svg>
                  Diff
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
